<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title">
        센서 측정 목록
        <span class="count-text">
          {{ sensorName ? sensorName + ' / ' : '' }}총 {{ resolvedTotalCount }} 건
        </span>
      </div>

      <div class="header-buttons">
        <!-- 여기만 수정 -->
        <button class="btn ghost" @click="onClickBack">목록으로</button>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="placeholder-text">

        <div class="table-section">
          <table v-if="tableRows.length" class="data-table">
            <colgroup>
              <col class="col-id" />
              <col class="col-measure-time" />
              <col class="col-measure-key" />
              <col class="col-measure-value" />
              <col class="col-unit" />
              <col class="col-collect-type" />
              <col class="col-equipment" />
              <col class="col-line-no" />
            </colgroup>

            <thead>
              <tr>
                <th>ID</th>
                <th>측정시각</th>
                <th>측정키</th>
                <th>측정값</th>
                <th>단위</th>
                <th>수집방식</th>
                <th>장비명</th>
                <th>라인번호</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tableRows" :key="row.id">
                <td>{{ row.id }}</td>
                <td>{{ formatDateTime(row.measuredAt) }}</td>
                <td>{{ row.measureKey || '-' }}</td>
                <td>{{ row.measureValue ?? '-' }}</td>
                <td>{{ row.unit || '-' }}</td>
                <td>{{ row.collectType || '-' }}</td>
                <td>{{ row.devName || row.deviceId || '-' }}</td>
                <td>{{ row.lineNumber || '-' }}</td>
              </tr>
            </tbody>
          </table>

          <div v-else class="table-empty">
            표시할 측정 데이터가 없습니다
          </div>
        </div>

        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />

      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
  name: 'SensorMeasurementList',

  components: {
    PaginationBar,
  },

  props: {
    sensorId: {
      type: Number,
      required: true,
    },
    sensorName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,
      loading: false,
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    },
  },

  mounted() {
    this.fetchSensorMeasurementList()
  },

  watch: {
    sensorId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.currentPage = 0
        this.fetchSensorMeasurementList()
      }
    },
  },

  methods: {
    async fetchSensorMeasurementList() {
      if (!this.sensorId) {
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
        this.currentPage = 0
        return
      }

      this.loading = true

      try {
        const response = await api.get('/app/sensorMeasurement/list', {
          params: {
            sensorId: this.sensorId,
            page: this.currentPage,
            size: this.size,
            orderBy: 'measuredAtDesc',
          },
        })

        const result = response.data
        const pageData = result?.result

        this.tableRows = Array.isArray(pageData?.content)
          ? pageData.content
          : []

        this.totalCount = Number(pageData?.totalElements ?? this.tableRows.length)
        this.totalPages = Number(pageData?.totalPages ?? 0)
        this.currentPage = Number(pageData?.number ?? this.currentPage)

        console.log('sensorMeasurement response.data =', result)
        console.log('sensorMeasurement tableRows =', this.tableRows)
      } catch (error) {
        console.error('센서 측정 목록 조회 실패', error)
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    goToPage(pageNo) {
      if (pageNo < 0 || pageNo >= this.totalPages || pageNo === this.currentPage) {
        return
      }

      this.currentPage = pageNo
      this.fetchSensorMeasurementList()
    },

    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const yy = String(date.getFullYear()).slice(-2)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },

    onClickBack() {
      this.$emit('back')
    },
  },
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-pagination.css";

/* =========================
   📋 TABLE WRAPPER
========================= */

.table-section {
  flex: 1;
  overflow: auto;
}

/* =========================
   📊 TABLE
========================= */

.data-table {
  width: 100%;
  min-width: 1000px;
}

/* 컬럼 width */
.col-id { width: 70px; }
.col-measure-time { width: 180px; }
.col-measure-key { width: 140px; }
.col-measure-value { width: 120px; }
.col-unit { width: 90px; }
.col-collect-type { width: 120px; }
.col-equipment { width: 180px; }
.col-line-no { width: 100px; }

/* =========================
   📱 EMPTY
========================= */

.table-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 220px;
  color: #9ca3af;
  font-size: 14px;
}

/* =========================
   TABLE ROW ACTIVE
========================= */

.data-table tbody tr {
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.data-table tbody tr:hover td {
  background: #f8fafc;
}

.data-table tbody tr:hover {
  box-shadow: inset 3px 0 0 #e58a13;
}
</style>